import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'tatouaz-xefloudizei-fagourizei-athina';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Είναι φυσιολογικό να ξεφλουδίζει το τατουάζ μου;", acceptedAnswer: { "@type": "Answer", text: "Ναι, απόλυτα. Το ξεφλούδισμα ξεκινάει τρεις έως πέντε μέρες μετά το τατουάζ και είναι φυσιολογικό μέρος της επούλωσης. Το τραυματισμένο επιφανειακό δέρμα αποβάλλεται για να αναγεννηθεί νέο." } },
    { "@type": "Question", name: "Χάνω μελάνι όταν ξεφλουδίζει το τατουάζ μου;", acceptedAnswer: { "@type": "Answer", text: "Όχι το πραγματικό σου τατουάζ. Λίγο μελάνι μπαίνει στην επιφανειακή στιβάδα και βγαίνει μαζί με τις φλούδες, αλλά το κύριο τατουάζ βρίσκεται πολύ πιο βαθιά και είναι απόλυτα ασφαλές." } },
    { "@type": "Question", name: "Μπορώ να ξύσω το τατουάζ μου;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Το ξύσιμο μπορεί να τραυματίσει το ευαίσθητο νέο δέρμα, να δημιουργήσει ουλές και να χαλάσει το σχέδιο. Χρησιμοποίησε ελαφριά χτυπήματα, ενυδατική ή απόσπαση προσοχής αντί για ξύσιμο." } },
    { "@type": "Question", name: "Πόσο κρατάει το ξεφλούδισμα και η φαγούρα;", acceptedAnswer: { "@type": "Answer", text: "Το ξεφλούδισμα κρατάει επτά έως δεκατέσσερις μέρες. Η έντονη φαγούρα κρατάει πέντε έως δέκα μέρες. Και τα δύο υποχωρούν σταδιακά με την πρόοδο της επούλωσης." } },
    { "@type": "Question", name: "Πότε να ανησυχήσω για το ξεφλούδισμα ή τη φαγούρα;", acceptedAnswer: { "@type": "Answer", text: "Αν συνοδεύονται από έντονο αυξανόμενο κοκκίνισμα, πύον, πυρετό, εξάνθημα, φουσκάλες ή αν παραμένουν έντονα για πολύ περισσότερο από το κανονικό, επικοινώνησε με τον καλλιτέχνη σου ή γιατρό." } },
  ],
};

export default function TatouazXefloudizeiFagourizeiAthina() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τατουάζ που Ξεφλουδίζει και Φαγουρίζει: Είναι Φυσιολογικό;",
      "description": "Οδηγός για την Αθήνα: γιατί ξεφλουδίζει και φαγουρίζει ένα φρέσκο τατουάζ, τι είναι φυσιολογικό, πώς διαχειρίζεσαι τη φαγούρα και πότε πρέπει να ανησυχήσεις.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-07",
      "dateModified": "2026-07-07",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-peel-faq', faqSchema);
    inject('schema-peel-article', articleSchema);
    return () => {
      document.getElementById('schema-peel-faq')?.remove();
      document.getElementById('schema-peel-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ Ξεφλουδίζει και Φαγουρίζει; | 210 Tattoo Athens</title>
        <meta name="description" content="Το φρέσκο τατουάζ σου ξεφλουδίζει και φαγουρίζει; Γιατί συμβαίνει, τι είναι φυσιολογικό, πώς διαχειρίζεσαι τη φαγούρα σωστά και πότε πρέπει να ανησυχήσεις." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Τατουάζ Ξεφλουδίζει και Φαγουρίζει; | 210 Tattoo Athens" />
        <meta property="og:description" content="Γιατί ξεφλουδίζει και φαγουρίζει ένα φρέσκο τατουάζ, τι είναι φυσιολογικό και πώς το διαχειρίζεσαι." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ Ξεφλουδίζει και Φαγουρίζει; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Τι είναι φυσιολογικό, πώς διαχειρίζεσαι τη φαγούρα και πότε να ανησυχήσεις." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Ξεφλούδισμα και Φαγούρα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Το τατουάζ ξεφλουδίζει και φαγουρίζει</span>
              <br />
              <span className="text-white">είναι φυσιολογικό;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">7 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Στη συντριπτική πλειοψηφία των περιπτώσεων, όλα είναι απόλυτα φυσιολογικά. Το ξεφλούδισμα και η φαγούρα είναι κομμάτια της επούλωσης που κάθε τατουάζ περνάει. Δες τι είναι φυσιολογικό, τι όχι και πώς να το διαχειριστείς.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* What happens */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι συμβαίνει στο δέρμα σου <span className="text-pink-500">κατά την επούλωση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Όταν ο tattoo artist δουλεύει, η βελόνα δημιουργεί χιλιάδες μικροσκοπικές πληγές, τοποθετώντας το μελάνι στο χόριο, το δεύτερο στρώμα του δέρματος. Το σώμα σου το αντιλαμβάνεται ως τραυματισμό και ενεργοποιεί άμεσα τους μηχανισμούς επούλωσης. Το ξεφλούδισμα και η φαγούρα είναι φυσικά μέρη αυτής της διαδικασίας.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Το πραγματικό σου τατουάζ βρίσκεται πολύ πιο βαθιά στο δέρμα, στο χόριο, και είναι απόλυτα ασφαλές. Το μελάνι που φεύγει με τις φλούδες είναι μόνο μια μικρή ποσότητα από την επιφανειακή στιβάδα, το κύριο τατουάζ παραμένει άθικτο. Για τη συνολική εικόνα της επούλωσης, δες τον οδηγό μας για το <Link to="/blog/tattoo-healing-time-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ</Link>.
            </p>
          </div>
        </section>

        {/* Peeling */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Το ξεφλούδισμα, <span className="text-pink-500">τι είναι φυσιολογικό</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Πότε αρχίζει', desc: 'Συνήθως τρεις έως πέντε μέρες μετά το τατουάζ, μόλις κλείσει η πρώτη επιφανειακή στιβάδα και ξεκινήσει η αποβολή του νεκρού δέρματος.' },
                { title: 'Πώς μοιάζει', desc: 'Λεπτές, ξερές νιφάδες σαν μετά από ηλιακό έγκαυμα, με λίγο χρώμα καθώς επιφανειακό μελάνι φεύγει μαζί τους. Αυτό τρομάζει, αλλά είναι φυσιολογικό.' },
                { title: 'Πόσο κρατάει', desc: 'Επτά έως δεκατέσσερις μέρες, ανάλογα με το μέγεθος, το σημείο και τον οργανισμό σου. Τα μικρά τατουάζ τελειώνουν σε μία εβδομάδα.' },
                { title: 'Γιατί συμβαίνει', desc: 'Το τραυματισμένο επιφανειακό δέρμα πρέπει να απομακρυνθεί για να αναγεννηθεί νέο, υγιές δέρμα από κάτω.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Η πιο κρίσιμη πληροφορία: μην τραβάς ποτέ τις φλούδες και μην ξύνεις. Αν τραβήξεις μια φλούδα πριν το κάτω δέρμα είναι έτοιμο, μπορείς να βγάλεις μαζί μελάνι και να δημιουργήσεις κενά στο σχέδιο. Άφησε το δέρμα να επουλωθεί με τον δικό του ρυθμό.
            </p>
          </div>
        </section>

        {/* Itching management */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Η φαγούρα και <span className="text-pink-500">πώς να τη διαχειριστείς</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Η φαγούρα ξεκινάει την ίδια περίοδο με το ξεφλούδισμα και κορυφώνεται στη φάση της έντονης αναγέννησης. Επειδή δεν μπορείς να ξύνεις, χρειάζεσαι εναλλακτικές που πραγματικά λειτουργούν.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Χτύπησε απαλά την περιοχή', desc: 'Ελαφρά χτυπηματάκια με την παλάμη γύρω από το τατουάζ ενεργοποιούν άλλους αισθητήρες και καλύπτουν τη φαγούρα για λίγα λεπτά, χωρίς ζημιά.' },
                { title: 'Βάλε λίγη ενυδατική', desc: 'Συχνά η φαγούρα προκαλείται από ξηρότητα. Μια λεπτή στρώση από την ενυδατική που σου πρότεινε ο καλλιτέχνης ανακουφίζει άμεσα.' },
                { title: 'Απόσπασε την προσοχή σου', desc: 'Η φαγούρα είναι πιο έντονη όταν κάθεσαι και τη σκέφτεσαι. Δούλεψε σε κάτι που σε απορροφά ή βγες βόλτα.' },
                { title: 'Φόρα άνετα ρούχα', desc: 'Ρούχα που δεν τρίβονται στην περιοχή. Η τριβή αυξάνει τη φαγούρα.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* When to worry */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε πρέπει <span className="text-pink-500">να ανησυχήσεις</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το ξεφλούδισμα δεν πρέπει να συνοδεύεται από έντονο, αυξανόμενο κοκκίνισμα που απλώνεται πέρα από την περιοχή, ούτε από πύον, δηλαδή κιτρινοπράσινο υγρό με άσχημη οσμή, ούτε από πυρετό. Η φαγούρα δεν πρέπει να συνοδεύεται από εξάνθημα ή φουσκάλες. Αν έχεις τέτοιες αντιδράσεις σε συγκεκριμένα χρώματα, μπορεί να είναι σημάδι αλλεργικής αντίδρασης στο μελάνι, κάτι σπάνιο αλλά υπαρκτό.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Σε κάθε περίπτωση που κάτι σε ανησυχεί, επικοινώνησε με τον καλλιτέχνη σου, και αν τα συμπτώματα είναι έντονα, συμβουλέψου γιατρό. Για να ξεχωρίσεις τη φυσιολογική επούλωση από κάτι σοβαρότερο, δες τον οδηγό μας για το <Link to="/blog/pos-katalabaineis-tatouaz-molynsi-athina" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί</Link>. Για τα υπόλοιπα, ακολούθησε τον <Link to="/blog/tattoo-aftercare-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">οδηγό φροντίδας μετά το studio</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θες ποιοτική δουλειά που <span className="text-pink-500">θα επουλωθεί ομαλά;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μήνυμα στο Instagram του στούντιο. Έμπειροι καλλιτέχνες, σωστή τεχνική τοποθέτησης μελανιού και αναλυτικές οδηγίες μεταφροντίδας που κάνουν την επούλωση πιο ήπια.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-healing-time-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πόσο Χρόνο Χρειάζεται να Επουλωθεί ένα Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Όλα τα στάδια επούλωσης και σωστή φροντίδα.</p>
              </Link>
              <Link to="/blog/pos-katalabaineis-tatouaz-molynsi-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Καταλαβαίνεις Μόλυνση σε Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Φυσιολογική επούλωση ή μόλυνση και τι να κάνεις.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/color-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Color Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
